import { IconInfoCircleFilled } from '@tabler/icons-react';
import NSIcon from 'bricks/NSIcon/NSIcon';
import {
    useState, useMemo, useCallback, useRef, useEffect,
} from 'react';
import { trackDatadogRumError } from 'utils/initDatadogRum';
import { useParams } from 'react-router-dom';
import assertNever from 'helpers/assertNever';
import invariant from 'helpers/invariant';
import { NSCard, NSSelect } from 'bricks';
import { useAuthContext } from 'contexts/AuthContext';
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal';
import { isLandDevelopmentModel } from 'helpers/proForma';
import useGetDealsWithProFormas from 'views/ProFormaTable/hooks/useGetDealsWithProFormas';
import { useGetProFormasByDeal } from 'queries/ProForma/useGetProFormasByDeal';
import useGenerateInvestmentMemoDeck from 'queries/InvestmentMemo/useGenerateInvestmentMemoDeck';
import useTaskStatus, { TaskStatusResponse } from 'queries/InvestmentMemo/useTaskStatus';
import useLatestInvestmentMemo from 'queries/InvestmentMemo/useLatestInvestmentMemo';
import useToast, { ToastType } from 'hooks/useToast';
import { PRO_FORMA_STATUS } from 'views/ProFormaHome/constants';
import InvestmentMemoSideNav from './InvestmentMemoSideNav/InvestmentMemoSideNav';
import InvestmentMemoSummary from './InvestmentMemoSections/InvestmentMemoSummary/InvestmentMemoSummary';
import InvestmentMemoDealOverview from './InvestmentMemoSections/InvestmentMemoDealOverview/InvestmentMemoDealOverview';
import InvestmentMemoFinancials from './InvestmentMemoSections/InvestmentMemoFinancials/InvestmentMemoFinancials';
import './InvestmentMemo.scss';
import { initialInvestmentMemoFormData, InvestmentMemoNavItem } from './constants';
import {
    InvestmentMemoFormData,
    SelectedProFormaOption,
    ProFormaOption,
    GroupedOption,
} from './types';
import { useInvestmentMemo } from './hooks/useInvestmentMemo';
import { useGenerateInvestmentMemoContent } from './hooks/useGenerateInvestmentMemoContent';
import { useValidation } from './hooks/useValidation';
import { hasEmptyRequiredFields } from './helpers';
import { InvestmentMemoContext } from './InvestmentMemoContext';

const SELECTED_PROFORMA_STORAGE_KEY = 'investmentMemo_selectedProForma';
const getDealScopedStorageKey = (dealId: number) => `investmentMemo_selectedProForma_deal_${dealId}`;

const PRO_FORMA_DISABLED_DURING_DECK_GENERATION_TOOLTIP =
    'Pro forma cannot be changed while deck is generating. Wait until deck generation has completed to change pro forma selection.';

const LAND_DEVELOPMENT_PRO_FORMA_TOOLTIP = 'Land Development coming soon!';

export const isLandDevProFormaOption = (option: ProFormaOption): boolean =>
    isLandDevelopmentModel(option.proFormaModelId);

export const renderProFormaOptionLabel = (option: ProFormaOption) => {
    if (!isLandDevProFormaOption(option)) {
        return option.label;
    }
    return (
        <div className="d-flex align-items-center">
            <span>{option.label}</span>
            <span className="InvestmentMemo__option-tooltip ml-2 d-flex align-items-center">
                <NSIcon icon={IconInfoCircleFilled} className="text-dark" />
                <span className="InvestmentMemo__option-tooltip__text">{LAND_DEVELOPMENT_PRO_FORMA_TOOLTIP}</span>
            </span>
        </div>
    );
};

const InvestmentMemo = () => {
    const { dealId: dealIdParam } = useParams<{ dealId: string }>();
    const { selectedOrganizationId } = useAuthContext();
    const { showToast, showError } = useToast();
    const [activeNav, setActiveNav] = useState(InvestmentMemoNavItem.Summary);
    const [formData, setFormData] = useState<InvestmentMemoFormData>(initialInvestmentMemoFormData);
    const [selectedProFormaOption, setSelectedProFormaOption] = useState<SelectedProFormaOption | undefined>(undefined);

    const [pendingProFormaOption, setPendingProFormaOption] = useState<SelectedProFormaOption | undefined>(undefined);
    const [isChangeModalOpen, setIsChangeModalOpen] = useState(false);
    const [pendingTaskId, setPendingTaskId] = useState<string | undefined>(undefined);
    const [hasAttemptedGeneration, setHasAttemptedGeneration] = useState(false);

    const contentRef = useRef<HTMLDivElement>(null);
    const {
        shouldShowError, getFieldError, hasSectionError, hasNavError,
    } = useValidation(formData, selectedProFormaOption, hasAttemptedGeneration);

    useEffect(() => {
        if (contentRef.current) {
            contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [activeNav]);

    const disableMemoGenerationButton = hasAttemptedGeneration && hasEmptyRequiredFields(formData);

    console.log(formData); // Keep for product for now
    invariant(selectedOrganizationId, 'Selected organization ID is required');

    // Fetch data based on context: deal-scoped or global
    const { data: dealsWithProFormas = [], isLoading: isLoadingDealsWithProFormas } = useGetDealsWithProFormas({
        orgId: selectedOrganizationId,
        statusId: PRO_FORMA_STATUS.SUBSTANTIALLY_COMPLETE,
        enabled: !dealIdParam,
    });

    const { data: dealProFormas = [], isLoading: isLoadingDealProFormas } = useGetProFormasByDeal(
        {
            dealId: Number(dealIdParam) || 0,
            proFormaStatusIds: [PRO_FORMA_STATUS.SUBSTANTIALLY_COMPLETE],
        },
        { enabled: !!dealIdParam && !Number.isNaN(Number(dealIdParam)) },
    );

    const { mutate: generateDeck, isPending: isGeneratingDeck } = useGenerateInvestmentMemoDeck();

    const {
        proFormaRoom,
        documentSuggestions,
        isLoadingProForma,
        isTitleAddressLoaded,
        isLoadingDocumentSuggestions,
    } = useInvestmentMemo(selectedProFormaOption, formData, setFormData);

    const addressFields = useMemo(() => ({
        address: formData.title.address,
        city: formData.title.city,
        state: formData.title.state,
        zipCode: formData.title.zipCode,
    }), [
        formData.title.address,
        formData.title.city,
        formData.title.state,
        formData.title.zipCode,
    ]);
    const coordinateFields = useMemo(() => ({
        useCoordinates: !!formData.title.useCoordinates,
        latitude: formData.title.latitude ?? null,
        longitude: formData.title.longitude ?? null,
    }), [
        formData.title.useCoordinates,
        formData.title.latitude,
        formData.title.longitude,
    ]);
    const { isGenerating, aiSuggestions } = useGenerateInvestmentMemoContent({
        selectedProFormaOption,
        organizationId: selectedOrganizationId,
        isTitleAddressLoaded,
        addressFields,
        coordinateFields,
        setFormData,
    });

    const handleGenerateDeck = (exportFormat: 'pdf' | 'pptx') => {
        const startTime = Date.now();
        invariant(selectedProFormaOption, 'Selected pro forma option is required');

        setHasAttemptedGeneration(true);

        if (hasEmptyRequiredFields(formData)) {
            showError('There are empty required fields. Please fill them and try again.');
            return;
        }

        generateDeck(
            {
                organizationId: selectedOrganizationId,
                dealId: selectedProFormaOption.value.dealId!,
                proFormaId: selectedProFormaOption.value.id!,
                exportAs: exportFormat,
                formData,
            },
            {
                onSuccess: response => {
                    const endTime = Date.now();
                    const durationSeconds = ((endTime - startTime) / 1000).toFixed(1);
                    const timestamp = new Date().toLocaleTimeString();
                    console.log(
                        `[${timestamp}] Investment Memo deck generation started! - `
                        + `Format: ${exportFormat}, Duration: ${durationSeconds}s, Task ID: ${response.taskId}`,
                    );

                    setPendingTaskId(response.taskId);

                    showToast(
                        (
                            <div>
                                <strong>Hang tight!</strong>
                                <br />
                                We&apos;re generating your memo and will save it to the deal&apos;s document library. This may take a few minutes.
                            </div>
                        ),
                        ToastType.Success,
                        { autoClose: 8000 },
                    );
                },
                onError: error => {
                    console.log(`Investment Memo deck generation failed - Format: ${exportFormat}, Error: ${error}`);
                    showError('There is an error generating this deck. Please try again.');
                },
            },
        );
    };

    const proFormaOptions: ProFormaOption[] | GroupedOption[] = useMemo(() => {
        if (dealIdParam) {
            const flatOptions: ProFormaOption[] = dealProFormas.map(proForma => ({
                label: proForma.name,
                value: proForma.id,
                dealId: Number(dealIdParam),
                proFormaId: proForma.id,
                proFormaModelId: proForma.proFormaModelId ?? null,
            }));
            return flatOptions;
        }

        const groupedOptions: GroupedOption[] = [];
        dealsWithProFormas.forEach(deal => {
            const proFormaSelectOptions = deal.proFormas.map(proForma => ({
                label: proForma.name,
                value: proForma.id,
                dealId: deal.id,
                proFormaId: proForma.id,
                proFormaModelId: proForma.proFormaModelId ?? null,
            }));

            if (proFormaSelectOptions.length > 0) {
                groupedOptions.push({
                    label: deal.dealName.toUpperCase(),
                    options: proFormaSelectOptions,
                });
            }
        });

        return groupedOptions;
    }, [dealProFormas, dealsWithProFormas, dealIdParam]);

    useEffect(() => {
        if (proFormaOptions.length > 0 && !selectedProFormaOption) {
            const storageKey = dealIdParam ? getDealScopedStorageKey(Number(dealIdParam)) : SELECTED_PROFORMA_STORAGE_KEY;

            try {
                const savedProForma = localStorage.getItem(storageKey);

                if (savedProForma) {
                    const { dealId, proFormaId } = JSON.parse(savedProForma);

                    let matchingProFormaOption: ProFormaOption | undefined;
                    if (dealIdParam) {
                        matchingProFormaOption = (proFormaOptions as ProFormaOption[]).find(
                            opt => opt.proFormaId === proFormaId && opt.dealId === dealId,
                        );
                    } else {
                        matchingProFormaOption = (proFormaOptions as GroupedOption[])
                            .flatMap(group => group.options)
                            .find(opt => opt.proFormaId === proFormaId && opt.dealId === dealId);
                    }

                    if (matchingProFormaOption) {
                        const selectedOption: SelectedProFormaOption = {
                            label: matchingProFormaOption.label,
                            value: {
                                type: 'proforma',
                                id: matchingProFormaOption.proFormaId,
                                dealId: matchingProFormaOption.dealId,
                            },
                        };
                        setSelectedProFormaOption(selectedOption);
                    } else {
                        localStorage.removeItem(storageKey);
                    }
                }
            } catch {
                localStorage.removeItem(storageKey);
            }
        }
    }, [proFormaOptions, selectedProFormaOption, dealIdParam]);

    const updateFormData = useCallback(<K extends keyof InvestmentMemoFormData>(
        section: K,
        data: Partial<InvestmentMemoFormData[K]>,
    ) => {
        setFormData(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                ...data,
            },
        }));
    }, []);

    const navigateToSection = (navItem: InvestmentMemoNavItem) => {
        if (selectedProFormaOption) setActiveNav(navItem);
    };

    const saveSelectedProFormaToLocalStorage = (proFormaOption: SelectedProFormaOption) => {
        const proFormaData = {
            dealId: proFormaOption.value.dealId,
            proFormaId: proFormaOption.value.id,
        };
        const storageKey = dealIdParam
            ? getDealScopedStorageKey(proFormaOption.value.dealId)
            : SELECTED_PROFORMA_STORAGE_KEY;
        localStorage.setItem(storageKey, JSON.stringify(proFormaData));
    };

    const handleProFormaSelection = (option: { label: string; value: number; dealId: number } | null) => {
        if (!option || !option.value) return;

        // Convert NSSelect option format back to the expected format
        const newSelectedProFormaOption: SelectedProFormaOption = {
            label: option.label,
            value: {
                type: 'proforma',
                id: option.value,
                dealId: option.dealId,
            },
        };

        if (selectedProFormaOption?.value.id === newSelectedProFormaOption.value.id) return;

        if (selectedProFormaOption) {
            setPendingProFormaOption(newSelectedProFormaOption);
            setIsChangeModalOpen(true);
        } else {
            setSelectedProFormaOption(newSelectedProFormaOption);
            saveSelectedProFormaToLocalStorage(newSelectedProFormaOption);
            setHasAttemptedGeneration(false);
        }
    };

    const handleConfirmChange = () => {
        if (pendingProFormaOption) {
            setSelectedProFormaOption(pendingProFormaOption);
            saveSelectedProFormaToLocalStorage(pendingProFormaOption);
            setHasAttemptedGeneration(false);
        }
        setIsChangeModalOpen(false);
        setPendingProFormaOption(undefined);
        setFormData(initialInvestmentMemoFormData);
    };

    const handleCancelChange = () => {
        setIsChangeModalOpen(false);
        setPendingProFormaOption(undefined);
    };

    const handleTaskPollingError = useCallback((error: Error) => {
        trackDatadogRumError(error);

        const errorMessage = error.message;
        if (errorMessage.includes('timeout')) {
            showError('Task is taking longer than expected. Please check the document library later or contact support if the issue persists.');
        } else {
            showError('Unable to check task status. The task may still be running, but status updates are unavailable.');
        }
    }, [showError]);

    const {
        data: latestInvestmentMemo,
        refetch: refetchLatestInvestmentMemo,
    } = useLatestInvestmentMemo({
        organizationId: selectedOrganizationId,
        selectedProFormaOption: selectedProFormaOption ? {
            dealId: selectedProFormaOption.value.dealId,
            proFormaId: selectedProFormaOption.value.id,
        } : undefined,
    });

    const currentTaskId = useMemo(() => pendingTaskId || latestInvestmentMemo?.taskId, [pendingTaskId, latestInvestmentMemo]);

    const { data: polledTaskStatus } = useTaskStatus({
        taskId: currentTaskId,
        organizationId: selectedOrganizationId,
        enabled:
            !!currentTaskId
            && (
                !latestInvestmentMemo
                || latestInvestmentMemo.taskId !== currentTaskId
                || (latestInvestmentMemo.taskStatus !== 'COMPLETED' && latestInvestmentMemo.taskStatus !== 'FAILED')
            ),
        onError: handleTaskPollingError,
    });

    const mergedTaskStatus: TaskStatusResponse | undefined = useMemo(() => {
        if (!currentTaskId) {
            return undefined;
        }

        const memoIsForCurrentTask = latestInvestmentMemo?.taskId === currentTaskId;

        if (memoIsForCurrentTask) {
            if (latestInvestmentMemo.taskStatus === 'COMPLETED') {
                return {
                    taskId: currentTaskId,
                    status: 'SUCCESS',
                    message: 'Investment memo generation completed',
                } satisfies TaskStatusResponse;
            }

            if (latestInvestmentMemo.taskStatus === 'FAILED') {
                return {
                    taskId: currentTaskId,
                    status: 'FAILURE',
                    message: 'Investment memo generation failed',
                    error: 'Investment memo generation failed',
                };
            }
        }

        return polledTaskStatus;
    }, [latestInvestmentMemo, currentTaskId, polledTaskStatus]);

    useEffect(() => {
        if (polledTaskStatus?.status === 'SUCCESS' || polledTaskStatus?.status === 'FAILURE') {
            refetchLatestInvestmentMemo();
        }
    }, [polledTaskStatus?.status, refetchLatestInvestmentMemo]);

    const isDeckPending = mergedTaskStatus?.status === 'PENDING' || mergedTaskStatus?.status === 'PROGRESS';

    // Convert selectedProFormaOption to NSSelect format
    const selectedNSSelectValue = useMemo(() => {
        if (!selectedProFormaOption) return null;
        return {
            label: selectedProFormaOption.label,
            value: selectedProFormaOption.value.id,
            dealId: selectedProFormaOption.value.dealId,
            proFormaId: selectedProFormaOption.value.id,
        };
    }, [selectedProFormaOption]);

    const renderSection = () => {
        switch (activeNav) {
            case InvestmentMemoNavItem.Summary:
                return (
                    <InvestmentMemoSummary
                        disabled={!selectedProFormaOption}
                        formData={formData}
                        updateFormData={updateFormData}
                        navigateToSection={navigateToSection}
                        isGeneratingAIContent={isGenerating}
                        aiSuggestions={aiSuggestions}
                    />
                );
            case InvestmentMemoNavItem.DealOverview:
                invariant(selectedProFormaOption, 'Selected pro forma option is required');
                return (
                    <InvestmentMemoDealOverview
                        selectedProFormaOption={selectedProFormaOption}
                        formData={formData}
                        updateFormData={updateFormData}
                        navigateToSection={navigateToSection}
                        isGeneratingAIContent={isGenerating}
                        aiSuggestions={aiSuggestions}
                    />
                );
            case InvestmentMemoNavItem.Financials:
                invariant(selectedProFormaOption, 'Selected pro forma option is required');
                return (
                    <InvestmentMemoFinancials
                        updateFormData={updateFormData}
                        proFormaRoom={proFormaRoom}
                    />
                );
            default:
                return assertNever(activeNav);
        }
    };

    return (
        <InvestmentMemoContext.Provider value={{
            documentSuggestions,
            isLoadingDocumentSuggestions,
            isLoadingProForma,
            selectedProForma: selectedProFormaOption,
            shouldShowError,
            getFieldError,
            hasSectionError,
            hasNavError,
            hasAttemptedGeneration,
        }}
        >
            <div className="InvestmentMemo d-flex overflow-hidden">
                <InvestmentMemoSideNav
                    activeNav={activeNav}
                    navigateToSection={navigateToSection}
                    selectedProFormaOption={selectedProFormaOption}
                    disableGenerationButton={disableMemoGenerationButton}
                    onGenerateDeck={handleGenerateDeck}
                    isGenerating={isGeneratingDeck}
                    organizationId={selectedOrganizationId}
                    isDeckPending={isDeckPending}
                    mergedTaskStatus={mergedTaskStatus}
                    latestInvestmentMemo={latestInvestmentMemo}
                />
                <div ref={contentRef} className="InvestmentMemo__content h-100 flex-grow-1 px-4">
                    <div className="d-flex justify-content-center pb-2 px-2">
                        <div className="w-75">
                            <NSCard className="mb-2 p-3">
                                <div className="text-primary font-13 mb-1 font-weight-bold">SELECT PRO FORMA</div>
                                <NSSelect
                                    id="proFormaSelect"
                                    name="proFormaSelect"
                                    options={proFormaOptions}
                                    value={selectedNSSelectValue}
                                    onChange={handleProFormaSelection}
                                    placeholder={dealIdParam ? 'Select pro forma' : 'Select deal and pro forma'}
                                    isDisabled={(dealIdParam ? isLoadingDealProFormas : isLoadingDealsWithProFormas) || isDeckPending}
                                    isClearable={false}
                                    isOptionDisabled={isLandDevProFormaOption}
                                    formatOptionLabel={renderProFormaOptionLabel}
                                    disabledTooltip={isDeckPending
                                        ? PRO_FORMA_DISABLED_DURING_DECK_GENERATION_TOOLTIP
                                        : undefined}
                                />
                            </NSCard>
                            <hr />
                        </div>
                    </div>
                    {renderSection()}
                </div>
                <ConfirmationModal
                    isOpen={isChangeModalOpen}
                    toggle={handleCancelChange}
                    onConfirm={handleConfirmChange}
                    onCancel={handleCancelChange}
                    danger
                    modalHeaderText="Change Pro Forma"
                    modalFooterButtonText="Confirm"
                    modalFooterCancelButtonText="Cancel"
                >
                    Are you sure you want to select a new pro forma for this investment memo? Doing so will overwrite any work so far.
                </ConfirmationModal>
            </div>
        </InvestmentMemoContext.Provider>
    );
};

export default InvestmentMemo;
